import styled from "@emotion/styled";
import { 
  Button, 
  Typography, 
  Box,
  Paper,
  Divider,
  CircularProgress,
  IconButton,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Cookies from "universal-cookie";
import { postApiServices } from "../../../apis/api";
import { apiRoutes } from "../../../apis/apiPath";
import { adminSinginInitialValues } from "../../../constants";
import { setUserData } from "../../../redux/MainReducer";
import { store } from "../../../redux/Store";
import { ROUTE_PATHS } from "../../../routes/routePath";
import { errorToast, successToast, sanitizeUserError } from "../../../utils/helper";
import PublicIcon from "@mui/icons-material/Public";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import LoginIcon from "@mui/icons-material/Login";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import SecurityIcon from "@mui/icons-material/Security";

// Theme colors
const PRIMARY_COLOR = "#7285b3";
const PRIMARY_DARK = "#5a6a94";
const ADMIN_COLOR = "#5c6bc0"; // Slightly different shade for admin
const ADMIN_DARK = "#3f51b5";

// Styled Components
const PageContainer = styled("div")({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  minHeight: "100vh",
  backgroundColor: "#f5f6fa",
  padding: "20px",
});

const LoginCard = styled(Paper)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: "40px",
  borderRadius: "16px",
  maxWidth: "420px",
  width: "100%",
  boxShadow: "0 10px 40px rgba(0, 0, 0, 0.1)",
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "4px",
    background: `linear-gradient(90deg, ${ADMIN_COLOR}, ${PRIMARY_COLOR})`,
  },
});

const InputWrapper = styled(Box)({
  position: "relative",
  width: "100%",
  marginBottom: "16px",
});

const StyledInput = styled("input")(({ hasError }) => ({
  padding: "14px 20px",
  paddingRight: "45px",
  width: "100%",
  borderRadius: "12px",
  fontSize: "15px",
  borderWidth: 1,
  borderStyle: "solid",
  borderColor: hasError ? "#d32f2f" : "#e0e0e0",
  fontFamily: "Poppins, sans-serif",
  backgroundColor: "#fafafa",
  boxSizing: "border-box",
  transition: "all 0.3s ease",
  "&:focus": {
    outline: "none",
    borderColor: hasError ? "#d32f2f" : ADMIN_COLOR,
    backgroundColor: "#fff",
    boxShadow: `0 0 0 3px rgba(92, 107, 192, 0.15)`,
  },
  "&:disabled": {
    backgroundColor: "#eee",
    cursor: "not-allowed",
  },
  "&::placeholder": {
    color: "#aaa",
  },
}));

const PasswordToggle = styled(IconButton)({
  position: "absolute",
  right: "8px",
  top: "50%",
  transform: "translateY(-50%)",
  color: "#999",
  "&:hover": {
    color: ADMIN_COLOR,
  },
});

const StyledButton = styled(Button)({
  padding: "14px 20px",
  width: "100%",
  borderRadius: "12px",
  color: "white",
  fontSize: "15px",
  fontWeight: 600,
  backgroundColor: ADMIN_COLOR,
  fontFamily: "Poppins, sans-serif",
  textTransform: "none",
  transition: "all 0.3s ease",
  "&:hover": {
    backgroundColor: ADMIN_DARK,
    transform: "translateY(-1px)",
    boxShadow: `0 4px 12px rgba(92, 107, 192, 0.4)`,
  },
  "&:disabled": {
    backgroundColor: "#ccc",
  },
});

const BackButton = styled(Button)({
  padding: "10px 20px",
  borderRadius: "12px",
  fontSize: "14px",
  fontWeight: 500,
  fontFamily: "Poppins, sans-serif",
  textTransform: "none",
  color: "#666",
  borderColor: "#e0e0e0",
  "&:hover": {
    borderColor: PRIMARY_COLOR,
    color: PRIMARY_COLOR,
    backgroundColor: "rgba(114, 133, 179, 0.05)",
  },
});

const ErrorText = styled("span")({
  color: "#d32f2f",
  fontSize: "12px",
  marginTop: "4px",
  display: "block",
  paddingLeft: "4px",
});

const MessageBox = styled("div")(({ type }) => ({
  width: "100%",
  padding: "12px 16px",
  borderRadius: "12px",
  marginBottom: "16px",
  fontSize: "14px",
  fontFamily: "Poppins, sans-serif",
  textAlign: "center",
  backgroundColor: type === "error" ? "#ffebee" : type === "info" ? "#e3f2fd" : "#e8f5e9",
  color: type === "error" ? "#c62828" : type === "info" ? "#1565c0" : "#2e7d32",
  border: `1px solid ${type === "error" ? "#ef9a9a" : type === "info" ? "#90caf9" : "#a5d6a7"}`,
}));

const AdminBadge = styled(Box)({
  display: "inline-flex",
  alignItems: "center",
  gap: "6px",
  padding: "6px 12px",
  borderRadius: "20px",
  backgroundColor: "rgba(92, 107, 192, 0.1)",
  color: ADMIN_COLOR,
  fontSize: "12px",
  fontWeight: 600,
  fontFamily: "Poppins, sans-serif",
  marginBottom: "16px",
});

const IconWrapper = styled(Box)({
  width: 80,
  height: 80,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: 16,
  backgroundColor: "rgba(92, 107, 192, 0.1)",
});

export const AdminSingin = () => {
  const cookies = new Cookies();
  const navigate = useNavigate();
  const [values, setValues] = useState(adminSinginInitialValues);
  const [touched, setTouched] = useState({});
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  const onChange = (e) => {
    const { name, value } = e.target;
    setValues((prev) => ({
      ...prev,
      [name]: value,
    }));
    
    // Clear error when typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
    
    // Clear message when typing
    if (message.text) {
      setMessage({ type: "", text: "" });
    }
  };

  const handleBlur = (name) => {
    setTouched((prev) => ({
      ...prev,
      [name]: true,
    }));
    validateField(name, values[name]);
  };

  const validateField = (name, value) => {
    let error = "";
    
    switch (name) {
      case "username":
        if (!value || value.trim() === "") {
          error = "Username is required";
        }
        break;
      case "password":
        if (!value || value === "") {
          error = "Password is required";
        }
        break;
      default:
        break;
    }
    
    setErrors((prev) => ({
      ...prev,
      [name]: error,
    }));
    
    return error;
  };

  const validateAllFields = () => {
    const fieldsToValidate = ["username", "password"];
    let isValid = true;
    const newErrors = {};
    const newTouched = {};

    fieldsToValidate.forEach((field) => {
      newTouched[field] = true;
      let error = "";
      
      if (!values[field] || values[field].trim() === "") {
        error = `${field === "username" ? "Username" : "Password"} is required`;
        isValid = false;
      }
      
      if (error) {
        newErrors[field] = error;
      }
    });

    setTouched(newTouched);
    setErrors(newErrors);
    
    return isValid;
  };

  const login = useMutation({
    mutationFn: () => postApiServices(apiRoutes.ADMIN_LOGIN, values),
    onSuccess: async ({ data }) => {
      if (!data?.success && data?.error) {
        setMessage({
          type: "error",
          text: sanitizeUserError(
            data.message,
            "We couldn't sign you in right now. Please try again in a few minutes. If this continues, contact support."
          ),
        });
        return;
      }

      cookies.set("token", data?.token, { path: "/" });
      cookies.set("userId", data?.userId, { path: "/" });
      cookies.set("userFullName", data?.userFullName, { path: "/" });
      cookies.set("defaultcompany", data?.defaultcompany, { path: "/" });
      
      if (data?.defaulttenant) {
        cookies.set("defaulttenant", data?.defaulttenant, { path: "/" });
      }
      
      store.dispatch(
        setUserData({
          ...data,
        })
      );
      
      successToast("Admin login successful!");
      navigate(ROUTE_PATHS.COMPANY_LIST);
    },
    onError: (error) => {
      const errorMessage = sanitizeUserError(
        error?.response?.data?.message || error?.response?.data,
        "We couldn't sign you in right now. Please try again in a few minutes. If this continues, contact support."
      );

      setMessage({
        type: "error",
        text: errorMessage,
      });
      errorToast(errorMessage);
    },
  });

  const handleSubmit = () => {
    setMessage({ type: "", text: "" });
    
    const isValid = validateAllFields();
    
    if (!isValid) {
      setMessage({
        type: "error",
        text: "Please fill all required fields",
      });
      return;
    }
    
    login.mutate();
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSubmit();
    }
  };

  const handleBackToLogin = () => {
    navigate(ROUTE_PATHS.LOGIN);
  };

  return (
    <PageContainer>
      <LoginCard elevation={0}>
        {/* Logo */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <PublicIcon sx={{ color: PRIMARY_COLOR, fontSize: 45, mr: 1 }} />
          <Typography
            variant="h5"
            sx={{ color: PRIMARY_COLOR, fontWeight: 700, fontFamily: "Poppins" }}
          >
            Premierdis
          </Typography>
        </Box>

        {/* Admin Badge */}
        <AdminBadge>
          <SecurityIcon sx={{ fontSize: 16 }} />
          System Administrator
        </AdminBadge>

        <IconWrapper>
          <AdminPanelSettingsIcon sx={{ fontSize: 40, color: ADMIN_COLOR }} />
        </IconWrapper>

        <Typography
          sx={{ 
            fontSize: "24px", 
            fontWeight: 600, 
            mb: 0.5, 
            fontFamily: "Poppins",
            color: "#333",
          }}
        >
          Admin Login
        </Typography>

        <Typography
          sx={{ 
            fontSize: "14px", 
            mb: 3, 
            fontFamily: "Poppins",
            color: "#888",
            textAlign: "center",
          }}
        >
          Enter your administrator credentials
        </Typography>

        {/* Message */}
        {message.text && (
          <MessageBox type={message.type}>
            {message.text}
          </MessageBox>
        )}

        {/* Form */}
        <Box sx={{ width: "100%" }}>
          {/* Username */}
          <InputWrapper>
            <StyledInput
              name="username"
              type="text"
              placeholder="Admin Username *"
              value={values?.username || ""}
              onChange={onChange}
              onBlur={() => handleBlur("username")}
              onKeyPress={handleKeyPress}
              hasError={touched.username && errors.username}
              disabled={login.isPending}
            />
            {touched.username && errors.username && (
              <ErrorText>{errors.username}</ErrorText>
            )}
          </InputWrapper>

          {/* Password */}
          <InputWrapper>
            <StyledInput
              name="password"
              type={showPassword ? "text" : "password"}
              placeholder="Admin Password *"
              value={values?.password || ""}
              onChange={onChange}
              onBlur={() => handleBlur("password")}
              onKeyPress={handleKeyPress}
              hasError={touched.password && errors.password}
              disabled={login.isPending}
            />
            <PasswordToggle
              size="small"
              onClick={() => setShowPassword(!showPassword)}
              disabled={login.isPending}
            >
              {showPassword ? (
                <VisibilityOffIcon fontSize="small" />
              ) : (
                <VisibilityIcon fontSize="small" />
              )}
            </PasswordToggle>
            {touched.password && errors.password && (
              <ErrorText>{errors.password}</ErrorText>
            )}
          </InputWrapper>

          {/* Submit Button */}
          <StyledButton 
            variant="contained" 
            onClick={handleSubmit}
            disabled={login.isPending}
            startIcon={!login.isPending && <LoginIcon />}
            sx={{ mt: 1 }}
          >
            {login.isPending ? (
              <>
                <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                Signing in...
              </>
            ) : (
              "Sign In as Admin"
            )}
          </StyledButton>
        </Box>

        {/* Divider */}
        <Box sx={{ width: "100%", my: 3, display: "flex", alignItems: "center" }}>
          <Divider sx={{ flexGrow: 1 }} />
        </Box>

        {/* Back to User Login */}
        {/* <BackButton
          variant="outlined"
          onClick={handleBackToLogin}
          startIcon={<ArrowBackIcon />}
          fullWidth
        >
          Back to User Login
        </BackButton> */}

        {/* Security Notice */}
        <Box 
          sx={{  
            p: 2, 
            backgroundColor: "rgba(92, 107, 192, 0.05)",
            borderRadius: 2,
            width: "100%",
          }}
        >
          <Typography
            sx={{ 
              fontSize: "11px", 
              color: "#666",
              fontFamily: "Poppins",
              textAlign: "center",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.5,
            }}
          >
            <SecurityIcon sx={{ fontSize: 14, color: ADMIN_COLOR }} />
            This is a restricted area. Unauthorized access is prohibited.
          </Typography>
        </Box>
      </LoginCard>
    </PageContainer>
  );
};